import React, { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import Modal from './Modal';
import { apiClient, SystemEmbeddingResponse, SystemEmbeddingCreateRequest } from '../lib/api';

interface SystemEmbeddingModalProps {
    isOpen: boolean;
    onClose: () => void;
    embedding: SystemEmbeddingResponse | null;
}

const SystemEmbeddingModal: React.FC<SystemEmbeddingModalProps> = ({
    isOpen,
    onClose,
    embedding
}) => {
    const [name, setName] = useState('');
    const [commandType, setCommandType] = useState('');
    const [content, setContent] = useState('');
    const [error, setError] = useState('');
    const queryClient = useQueryClient();

    // Initialize form with embedding data when modal opens
    useEffect(() => {
        if (embedding && isOpen) {
            setName(embedding.name);
            setCommandType(embedding.command_type);
            setContent(embedding.text_content);
            setError('');
        } else if (isOpen) {
            setName('');
            setCommandType('');
            setContent('');
            setError('');
        }
    }, [embedding, isOpen]);

    const mutation = useMutation({
        mutationFn: (data: SystemEmbeddingCreateRequest) => {
            if (embedding) {
                return apiClient.updateSystemEmbedding(embedding.id, data);
            }
            return apiClient.createSystemEmbedding(data);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['system-embeddings'] });
            onClose();
            setError('');
        },
        onError: (error: Error) => {
            setError(error.message || 'Failed to save system embedding');
        },
    });

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        if (!name.trim()) {
            setError('Name is required');
            return;
        }
        if (!content.trim()) {
            setError('Content is required');
            return;
        }

        mutation.mutate({
            name: name.trim(),
            command_type: commandType,
            content: content.trim()
        });
    };

    const handleClose = () => {
        if (!mutation.isLoading) {
            onClose();
            setError('');
        }
    };

    const footer = (
        <>
            <button
                type="button"
                className="mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                onClick={handleClose}
                disabled={mutation.isLoading}
            >
                Cancel
            </button>
            <button
                type="submit"
                form="system-embedding-form"
                className="inline-flex w-full justify-center rounded-md border border-transparent bg-primary-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={mutation.isLoading}
            >
                {mutation.isLoading ? 'Saving...' : embedding ? 'Update' : 'Create'}
            </button>
        </>
    );

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            title={embedding ? 'Edit System Embedding' : 'New System Embedding'}
            footer={footer}
            size="lg"
        >
            <form id="system-embedding-form" onSubmit={handleSubmit} className="space-y-4">
                {error && (
                    <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                        {error}
                    </div>
                )}

                <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                        Name *
                    </label>
                    <input
                        type="text"
                        id="name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:outline-none focus:border-gray-400 sm:text-sm px-3 py-2"
                        placeholder="e.g., Default Welcome Response"
                        disabled={mutation.isLoading}
                        required
                    />
                </div>

                <div>
                    <label htmlFor="command-type" className="block text-sm font-medium text-gray-700">
                        Command Type *
                    </label>
                    <input
                        type="text"
                        id="command-type"
                        value={commandType}
                        onChange={(e) => setCommandType(e.target.value)}
                        className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:outline-none focus:border-gray-400 sm:text-sm px-3 py-2"
                        placeholder="e.g., appointment"
                        disabled={mutation.isLoading}
                        required
                    />
                </div>

                <div>
                    <label htmlFor="content" className="block text-sm font-medium text-gray-700">
                        Content *
                    </label>
                    <textarea
                        id="content"
                        rows={5}
                        value={content}
                        onChange={(e) => setContent(e.target.value)}
                        className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:outline-none focus:border-gray-400 sm:text-sm px-3 py-2"
                        placeholder="Enter the text content to be embedded..."
                        disabled={mutation.isLoading}
                        required
                    ></textarea>
                    <p className="mt-1 text-xs text-gray-500">
                        This content will be vectorized and used for similarity search.
                    </p>
                </div>
            </form>
        </Modal>
    );
};

export default SystemEmbeddingModal;
